<template>
	<Tour.Root :tour="tour">
		<Teleport to="body">
			<Tour.Backdrop class="backdrop" />
			<svg v-if="highlightRects.length" class="highlight-overlay" aria-hidden="true">
				<defs>
					<mask id="app-tour-highlight-mask">
						<rect width="100%" height="100%" fill="white" />
						<rect v-for="(rect, index) in highlightRects" :key="index" v-bind="rect" fill="black" />
					</mask>
				</defs>
				<rect class="shade" width="100%" height="100%" mask="url(#app-tour-highlight-mask)" />
				<rect v-for="(rect, index) in highlightRects" :key="index" class="outline" v-bind="rect" />
			</svg>
			<Tour.Positioner class="positioner">
				<Tour.Content class="app-tour">
					<Tour.Arrow class="arrow">
						<Tour.ArrowTip class="arrow-tip" />
					</Tour.Arrow>

					<header class="header">
						<Tour.Title class="title">{{ tour.step?.title }}</Tour.Title>
						<Tour.CloseTrigger class="close" :aria-label="t('appTour.actions.close')">
							<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
						</Tour.CloseTrigger>
					</header>

					<Tour.Description class="description">{{ tour.step?.description }}</Tour.Description>

					<div
						v-if="tour.totalSteps > 1"
						class="progress"
						role="progressbar"
						:aria-valuenow="tour.getProgressPercent()"
						aria-valuemin="0"
						aria-valuemax="100"
						:aria-valuetext="tour.getProgressText()"
					>
						<div class="progress-fill" :style="{ width: `${tour.getProgressPercent()}%` }" />
					</div>

					<div class="actions">
						<Tour.Actions v-slot="actions">
							<Tour.ActionTrigger v-for="action in actions" :key="action.label" :action="action" as-child>
								<Button :variant="action.action === 'prev' ? 'secondary' : 'primary'" size="s">
									{{ action.label }}
								</Button>
							</Tour.ActionTrigger>
						</Tour.Actions>
					</div>
				</Tour.Content>
			</Tour.Positioner>
		</Teleport>
	</Tour.Root>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useMediaQuery } from "@vueuse/core"
import { Tour, useTour } from "@ark-ui/vue"
import type { TourStepDetails } from "@ark-ui/vue"
import AppIcon from "@/components/AppIcon.vue"
import Button from "@/components/ui/Button.vue"
import { IconName } from "@/components/icons/registry"
import { ParcelHintStatus, useUIStore } from "@/stores/uiStore"
import { Permission, UseRootStore } from "@/model/RootStore"
import { Route } from "@/utils/routing"

type StepAction = NonNullable<TourStepDetails["actions"]>[number]

enum AppTourStep {
	Welcome = "welcome",
	Navigation = "navigation",
	MapSearch = "map-search",
	Layers = "layers",
	FilterableLayers = "filterable-layers",
	MapControls = "map-controls",
	ViewportOpportunities = "viewport-opportunities",
	ParcelSelect = "parcel-select",
	ParcelOverview = "parcel-overview",
	ParcelDetails = "parcel-details",
	Activity = "activity",
	Tables = "tables",
	Hunting = "hunting",
	End = "end",
	Reminder = "reminder",
}

const SEEN_STORAGE_KEY_PREFIX = "app-tour-seen"

interface TargetedStep {
	id: AppTourStep
	placement: TourStepDetails["placement"]
}

const DESKTOP_STEPS: TargetedStep[] = [
	{ id: AppTourStep.Navigation, placement: "right" },
	{ id: AppTourStep.Activity, placement: "right" },
	{ id: AppTourStep.Hunting, placement: "right" },
	{ id: AppTourStep.Tables, placement: "right" },
	{ id: AppTourStep.Layers, placement: "right" },
	{ id: AppTourStep.FilterableLayers, placement: "right" },
	{ id: AppTourStep.MapSearch, placement: "bottom" },
	{ id: AppTourStep.MapControls, placement: "left" },
	{ id: AppTourStep.ViewportOpportunities, placement: "left" },
	{ id: AppTourStep.ParcelSelect, placement: "top" },
	{ id: AppTourStep.ParcelOverview, placement: "left" },
	{ id: AppTourStep.ParcelDetails, placement: "left" },
]

const MOBILE_STEPS: TargetedStep[] = [
	{ id: AppTourStep.Navigation, placement: "top" },
	{ id: AppTourStep.Activity, placement: "top-start" },
	{ id: AppTourStep.Hunting, placement: "top" },
	{ id: AppTourStep.MapSearch, placement: "bottom" },
	{ id: AppTourStep.MapControls, placement: "top-end" },
	{ id: AppTourStep.Layers, placement: "top-start" },
]

enum SearchItem {
	Address = "address",
	Parcel = "parcel",
	Transaction = "transaction",
	Opportunity = "opportunity",
}

const STEPS_MENTIONING_TRANSACTIONS = [AppTourStep.Layers, AppTourStep.FilterableLayers]

const PARCEL_CARD_STEPS = [AppTourStep.ParcelOverview, AppTourStep.ParcelDetails]
const STEPS_REVEALED_DURING_TOUR = [AppTourStep.ParcelSelect, ...PARCEL_CARD_STEPS]
const STEPS_WITH_SINGLE_HIGHLIGHT = [AppTourStep.ParcelOverview]
const STEPS_WITHOUT_HIGHLIGHT = [AppTourStep.ParcelSelect]

const HIGHLIGHT_PADDING = 4
const HIGHLIGHT_RADIUS = 4

interface HighlightRect {
	x: number
	y: number
	width: number
	height: number
	rx: number
}

const { t, locale } = useI18n()
const rootStore = UseRootStore()
const uiStore = useUIStore()
const route = useRoute()
const { isAppTourActive, parcelHintStatus, isExtendedAccessWelcomePending } = storeToRefs(uiStore)
const isMobile = useMediaQuery("(max-width: 768px)")

const tour = useTour({
	closeOnInteractOutside: false,
	onStatusChange: handleStatusChange,
	translations: {
		progressText: ({ current, total }) => t("appTour.progress", { current: current + 1, total }),
	},
})

function isVisible(element: HTMLElement): boolean {
	return element.getClientRects().length > 0
}

function findVisibleTarget(id: AppTourStep): HTMLElement | null {
	return [...document.querySelectorAll<HTMLElement>(`[data-tour="${id}"]`)].find(isVisible) ?? null
}

function findTooltipAnchor(id: AppTourStep): HTMLElement | null {
	const relatedElements = [...document.querySelectorAll<HTMLElement>(`[data-tour-related~="${id}"]`)]
	return relatedElements.filter(isVisible).at(-1) ?? findVisibleTarget(id)
}

const highlightRects = ref<HighlightRect[]>([])
let stepFrame = 0

function findHighlightedElements(id: string): HTMLElement[] {
	return [...document.querySelectorAll<HTMLElement>(`[data-tour="${id}"], [data-tour-related~="${id}"]`)]
}

function toHighlightRect(element: HTMLElement): HighlightRect {
	const { x, y, width, height } = element.getBoundingClientRect()
	return {
		x: x - HIGHLIGHT_PADDING,
		y: y - HIGHLIGHT_PADDING,
		width: width + 2 * HIGHLIGHT_PADDING,
		height: height + 2 * HIGHLIGHT_PADDING,
		rx: HIGHLIGHT_RADIUS,
	}
}

function mergeHighlightRects(rects: HighlightRect[]): HighlightRect[] {
	if (rects.length === 0) return rects
	const left = Math.min(...rects.map(rect => rect.x))
	const top = Math.min(...rects.map(rect => rect.y))
	const right = Math.max(...rects.map(rect => rect.x + rect.width))
	const bottom = Math.max(...rects.map(rect => rect.y + rect.height))
	return [{ x: left, y: top, width: right - left, height: bottom - top, rx: HIGHLIGHT_RADIUS }]
}

function syncHighlights(stepId: string | null): void {
	const isHighlighted = stepId != null && !STEPS_WITHOUT_HIGHLIGHT.includes(stepId as AppTourStep)
	const elementRects = isHighlighted ? findHighlightedElements(stepId).filter(isVisible).map(toHighlightRect) : []
	const isSingleHighlight = STEPS_WITH_SINGLE_HIGHLIGHT.includes(stepId as AppTourStep)
	const rects = isSingleHighlight ? mergeHighlightRects(elementRects) : elementRects
	if (JSON.stringify(rects) !== JSON.stringify(highlightRects.value)) highlightRects.value = rects
}

function syncLockedControls(stepId: string | null): void {
	for (const control of document.querySelectorAll<HTMLElement>("[data-tour-lock]")) {
		const isLocked = stepId != null && (control.dataset.tourLock ?? "").split(" ").includes(stepId)
		if (control.inert !== isLocked) control.inert = isLocked
	}
}

function trackStep(): void {
	const step = tour.value.step
	const stepId = step?.type === "tooltip" ? step.id : null
	syncHighlights(stepId)
	syncLockedControls(stepId)
	stepFrame = requestAnimationFrame(trackStep)
}

function stopTrackingStep(): void {
	cancelAnimationFrame(stepFrame)
	syncHighlights(null)
	syncLockedControls(null)
}

function followTarget(id: AppTourStep): TourStepDetails["effect"] {
	return ({ show }) => {
		let currentTarget: HTMLElement | null = null
		const syncTarget = () => {
			const target = findTooltipAnchor(id)
			if (!target || target === currentTarget) return
			currentTarget = target
			show()
		}

		syncTarget()
		const observer = new MutationObserver(syncTarget)
		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}
}

function hasPermission(permission: Permission): boolean {
	return Boolean(rootStore.currentUser?.has(permission))
}

function describeMapSearch(): string {
	const items = [SearchItem.Address, SearchItem.Parcel]
	if (hasPermission(Permission.TransactionRead)) items.push(SearchItem.Transaction)
	if (hasPermission(Permission.OpportunityRead)) items.push(SearchItem.Opportunity)

	const labels = items.map(item => t(`appTour.steps.${AppTourStep.MapSearch}.items.${item}`))
	const list = new Intl.ListFormat(locale.value, { type: "disjunction" }).format(labels)
	return t(`appTour.steps.${AppTourStep.MapSearch}.description`, { items: list })
}

function describeStep(id: AppTourStep): string {
	if (id === AppTourStep.MapSearch) return describeMapSearch()
	if (id === AppTourStep.Navigation) {
		const hasProjects = Boolean(findVisibleTarget(AppTourStep.Hunting))
		if (isMobile.value) {
			return t(`appTour.steps.${id}.${hasProjects ? "mobileDescriptionWithProjects" : "mobileDescription"}`)
		}
		if (hasProjects) return t(`appTour.steps.${id}.descriptionWithProjects`)
	}
	if (STEPS_MENTIONING_TRANSACTIONS.includes(id) && hasPermission(Permission.TransactionRead)) {
		return t(`appTour.steps.${id}.descriptionWithTransactions`)
	}
	return t(`appTour.steps.${id}.description`)
}

function showParcelHint(actionsWithCard: StepAction[], actionsWithoutCard: StepAction[]): TourStepDetails["effect"] {
	return ({ show, next, update }) => {
		let isCardVisible = findVisibleTarget(AppTourStep.ParcelOverview) != null
		update({ actions: isCardVisible ? actionsWithCard : actionsWithoutCard })

		const stopWatchingHint = watch(parcelHintStatus, status => {
			if (status === ParcelHintStatus.Shown) show()
			if (status === ParcelHintStatus.NotFound) {
				update({
					type: "floating",
					description: t(`appTour.steps.${AppTourStep.ParcelSelect}.notFoundDescription`),
				})
				show()
			}
		})
		uiStore.requestParcelHint()

		const observer = new MutationObserver(() => {
			const wasCardVisible = isCardVisible
			isCardVisible = findVisibleTarget(AppTourStep.ParcelOverview) != null
			if (isCardVisible && !wasCardVisible) next()
			if (!isCardVisible && wasCardVisible) update({ actions: actionsWithoutCard })
		})
		observer.observe(document.body, { childList: true, subtree: true })

		return () => {
			stopWatchingHint()
			observer.disconnect()
			uiStore.clearParcelHint()
		}
	}
}

function buildParcelSelectStep(
	placement: TourStepDetails["placement"],
	skipToStepId: string,
	prev: StepAction,
	next: StepAction
): TourStepDetails {
	const skip: StepAction = { label: t("appTour.actions.skip"), action: ({ goto }) => goto(skipToStepId) }
	return {
		id: AppTourStep.ParcelSelect,
		type: "tooltip",
		placement,
		target: () => findVisibleTarget(AppTourStep.ParcelSelect),
		effect: showParcelHint([prev, next], [prev, skip]),
		title: t(`appTour.steps.${AppTourStep.ParcelSelect}.title`),
		description: t(`appTour.steps.${AppTourStep.ParcelSelect}.description`),
		actions: [prev, skip],
	}
}

function buildSteps(): TourStepDetails[] {
	const next: StepAction = { label: t("appTour.actions.next"), action: "next" }
	const prev: StepAction = { label: t("appTour.actions.prev"), action: "prev" }

	const steps = (isMobile.value ? MOBILE_STEPS : DESKTOP_STEPS).filter(
		({ id }) => STEPS_REVEALED_DURING_TOUR.includes(id) || findVisibleTarget(id)
	)
	const targetedSteps: TourStepDetails[] = steps.map(({ id, placement }, index) => {
		if (id === AppTourStep.ParcelSelect) {
			const skipToStepId =
				steps.slice(index + 1).find(step => !PARCEL_CARD_STEPS.includes(step.id))?.id ?? AppTourStep.End
			return buildParcelSelectStep(placement, skipToStepId, prev, next)
		}
		return {
			id,
			type: "tooltip",
			placement,
			target: () => findTooltipAnchor(id),
			effect: followTarget(id),
			title: t(`appTour.steps.${id}.title`),
			description: describeStep(id),
			actions: [prev, next],
		}
	})

	return [
		{
			id: AppTourStep.Welcome,
			type: "dialog",
			title: t(`appTour.steps.${AppTourStep.Welcome}.title`),
			description: t(`appTour.steps.${AppTourStep.Welcome}.description`),
			actions: [{ label: t("appTour.actions.start"), action: "next" }],
		},
		...targetedSteps,
		{
			id: AppTourStep.End,
			type: "dialog",
			title: t(`appTour.steps.${AppTourStep.End}.title`),
			description: t(`appTour.steps.${AppTourStep.End}.${isMobile.value ? "mobileDescription" : "description"}`),
			actions: [prev, { label: t("appTour.actions.finish"), action: "dismiss" }],
		},
	]
}

function buildReminderStep(): TourStepDetails {
	return {
		id: AppTourStep.Reminder,
		type: "tooltip",
		placement: isMobile.value ? "top-end" : "right-end",
		target: () => findVisibleTarget(AppTourStep.Reminder),
		title: t(`appTour.steps.${AppTourStep.Reminder}.title`),
		description: t(`appTour.steps.${AppTourStep.Reminder}.${isMobile.value ? "mobileDescription" : "description"}`),
		actions: [{ label: t("appTour.actions.gotIt"), action: "dismiss" }],
	}
}

function startTour(steps: TourStepDetails[]): void {
	tour.value.setSteps(steps)
	tour.value.start()
	trackStep()
}

let isAutoLaunched = false

function seenStorageKey(userId: string): string {
	return `${SEEN_STORAGE_KEY_PREFIX}:${userId}`
}

watch(
	() => {
		const userId = rootStore.currentUser?.getInfo()?.id
		const isOnMap = route.name === Route.Home && !route.query.table
		return rootStore.CurrentUserDetails && isOnMap && !isExtendedAccessWelcomePending.value ? userId : undefined
	},
	userId => {
		if (!userId || isAppTourActive.value || localStorage.getItem(seenStorageKey(userId))) return

		localStorage.setItem(seenStorageKey(userId), new Date().toISOString())
		isAutoLaunched = true
		uiStore.startAppTour()
	},
	{ immediate: true }
)

watch(isAppTourActive, async isActive => {
	if (!isActive) return

	uiStore.openDrawer()
	await nextTick()
	startTour(buildSteps())
})

async function handleStatusChange({ status, stepId }: { status: string; stepId: string | null }): Promise<void> {
	if (status === "started") return
	stopTrackingStep()

	const shouldShowReminder =
		isAutoLaunched &&
		stepId !== AppTourStep.End &&
		stepId !== AppTourStep.Reminder &&
		findVisibleTarget(AppTourStep.Reminder) != null
	isAutoLaunched = false

	if (!shouldShowReminder) {
		uiStore.stopAppTour()
		return
	}

	await nextTick()
	startTour([buildReminderStep()])
}
</script>

<style>
[data-tour-lock][inert] {
	opacity: 0.4;
}
</style>

<style scoped>
.backdrop {
	z-index: 1000;
	background-color: var(--overlay-backdrop);

	&[data-type="tooltip"],
	&[data-type="floating"] {
		display: none;
	}
}

.highlight-overlay {
	position: fixed;
	inset: 0;
	z-index: 1000;
	width: 100%;
	height: 100%;
	pointer-events: none;

	.shade {
		fill: var(--overlay-backdrop);
	}

	.outline {
		fill: none;
		stroke: var(--primary-green);
	}
}

.positioner {
	--tour-z-index: 1000;
	z-index: 1001;

	&[data-type="dialog"] {
		position: fixed;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	&[data-type="floating"] {
		position: fixed;
		bottom: var(--spacing-lg);
		left: 50%;
		transform: translateX(-50%);
	}
}

.app-tour {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	width: 320px;
	max-width: calc(100vw - 2 * var(--spacing-md));
	padding: var(--spacing-md);
	border-radius: var(--border-radius-lg);
	background-color: var(--neutral-0);
	box-shadow: var(--shadow-xl);

	.arrow {
		--arrow-size: var(--space-12);
		--arrow-background: var(--neutral-0);
	}

	.header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);
	}

	.progress {
		height: var(--space-4);
		margin-top: var(--spacing-sm);
		border-radius: var(--border-radius-full);
		background-color: var(--neutral-100);
		overflow: hidden;

		.progress-fill {
			height: 100%;
			border-radius: inherit;
			background-color: var(--primary-green);
			transition: width 0.3s ease;
		}
	}

	.close {
		display: inline-flex;
		padding: 0;
		border: none;
		background: none;
		color: var(--neutral-600);
		cursor: pointer;
	}

	.title {
		margin: 0;
		font-size: var(--font-size-medium);
		font-weight: var(--font-weight-bold);
		color: var(--neutral-800);
	}

	.description {
		margin: 0;
		font-size: var(--font-size-small);
		line-height: var(--line-height-relaxed);
		color: var(--neutral-700);
	}

	.actions {
		display: flex;
		justify-content: flex-end;
		gap: var(--spacing-sm);
		padding-top: var(--spacing-sm);

		& > * {
			flex: 1;
			min-width: 0;
		}
	}
}
</style>
